import { useEffect, useState } from 'react'
import {
  type DeviceInfo,
  enumerateDevices,
  onDeviceChange,
  requestMediaPermissions,
  supportsAudioOutputSelection,
} from '@/shared/lib/livekit'
import { useDeviceSelection } from '../model/device-store'

interface MediaDevicesState {
  cameras: DeviceInfo[]
  microphones: DeviceInfo[]
  speakers: DeviceInfo[]
  ready: boolean
  error: string | null
}

// Перечисление устройств + реакция на hot-plug. Лейблы пусты без разрешения —
// сначала пробный getUserMedia (сразу глушим треки), затем enumerate.
export function useMediaDevices(): MediaDevicesState {
  const [state, setState] = useState<MediaDevicesState>({
    cameras: [],
    microphones: [],
    speakers: [],
    ready: false,
    error: null,
  })

  useEffect(() => {
    let cancelled = false

    async function probe(constraints: { audio?: boolean; video?: boolean }): Promise<boolean> {
      try {
        const stream = await requestMediaPermissions(constraints)
        for (const track of stream.getTracks()) track.stop()
        return true
      } catch {
        return false
      }
    }

    async function load() {
      // Общий запрос — happy path в один промпт. Он падает целиком, даже когда
      // недоступно что-то одно (ноутбук без камеры, отключённая вебка), поэтому
      // на отказе перепроверяем по отдельности: потерять микрофон вместе с
      // камерой — значит не пустить человека в звонок вообще.
      let audioOk = await probe({ audio: true, video: true })
      let videoOk = audioOk
      if (!audioOk) {
        audioOk = await probe({ audio: true })
        videoOk = await probe({ video: true })
      }

      const devices = await enumerateDevices().catch(() => [] as DeviceInfo[])
      if (cancelled) return
      // Выбор, за которым больше нет железа, возвращается на системное — иначе
      // селект показывает «Системное по умолчанию», а захват идёт со старого id.
      useDeviceSelection.getState().keepPresent(devices)

      let error: string | null = null
      if (!audioOk && !videoOk) {
        error = 'Нет доступа к камере и микрофону. Разрешите доступ в настройках браузера.'
      } else if (!audioOk) {
        error = 'Микрофон недоступен: вас не будет слышно. Проверьте доступ в настройках браузера.'
      } else if (!videoOk) {
        error = 'Камера недоступна: войти можно без видео.'
      }

      setState({
        cameras: devices.filter((d) => d.kind === 'videoinput'),
        microphones: devices.filter((d) => d.kind === 'audioinput'),
        // Без setSinkId выбор вывода ни на что не влияет — списка не даём,
        // чтобы селект не обещал того, чего не сделает.
        speakers: supportsAudioOutputSelection()
          ? devices.filter((d) => d.kind === 'audiooutput')
          : [],
        ready: true,
        error,
      })
    }

    void load()
    const off = onDeviceChange(() => void load())
    return () => {
      cancelled = true
      off()
    }
  }, [])

  return state
}
